/**
 * Utilities para transformación de datos entre la API y el frontend
 * Maneja conversiones, mapeos y normalizaciones de datos
 * NOTA: Este archivo usa las nuevas funciones de normalización centralizadas
 */

import moment from 'moment';
import { normalizeDataObject, normalizeDataArray } from './normalizationUtils';
import * as catalogoVehiculosApiService from '../services/catalogoVehiculosApiService';

/**
 * Transformaciones para Clientes
 */
export const clienteTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toApi: (clienteData) => {
    // Separar nombre completo si viene como un solo campo
    let nombre = clienteData.nombre || '';
    let apellido = clienteData.apellido || '';
    
    if (clienteData.Nombre && !nombre && !apellido) {
      const nombreCompleto = clienteData.Nombre.trim().split(' ');
      nombre = nombreCompleto[0] || '';
      apellido = nombreCompleto.slice(1).join(' ') || '';
    }
    
    return {
      nombre: nombre || clienteData.Nombre?.split(' ')[0] || '',
      apellido: apellido || clienteData.Apellido || '',
      email: clienteData.email || clienteData.Correo || '',
      telefono: (clienteData.telefono || clienteData.Telefono || '').toString(),
      direccion: clienteData.direccion || clienteData.Direccion || '',
      rut: clienteData.rut || clienteData.RUT || '',
      clerk_id: clienteData.clerk_id,
      activo: clienteData.activo !== undefined ? clienteData.activo : true
    };
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  fromApi: (apiData) => {
    if (!apiData) return null;
    
    // Usar normalización centralizada
    const normalized = normalizeDataObject(apiData, 'cliente');
    
    // Agregar campos de compatibilidad específicos para clientes
    if (normalized.nombre && normalized.apellido) {
      const nombreCompleto = `${normalized.nombre} ${normalized.apellido}`.trim();
      normalized.Nombre = nombreCompleto;
      normalized.nombreCompleto = nombreCompleto;
      normalized.NombreCompleto = nombreCompleto;
    }
    
    return normalized;
  },

  /**
   * Transforma lista de clientes
   */
  fromApiList: (apiList) => {
    if (!Array.isArray(apiList)) return [];
    return apiList.map(clienteTransforms.fromApi);
  }
};

/**
 * Transformaciones para Citas
 */
export const citaTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toApi: (citaData, user = null) => {
    return {
      patente: Array.isArray(citaData.Patente) ? citaData.Patente[0] : (citaData.patente || citaData.Patente),
      cliente_id: citaData.cliente_id || citaData.ClienteID || (user?.TipoUsuario === 'Cliente' ? user.ClienteID : null),
      sucursal_id: citaData.sucursal_id || (Array.isArray(citaData.Sucursal) ? citaData.Sucursal[0] : citaData.Sucursal),
      fecha: citaData.fecha || citaData.Fecha || (citaData.start ? moment(citaData.start).format('YYYY-MM-DD') : null),
      hora: citaData.hora || citaData.Hora || (citaData.start ? moment(citaData.start).format('HH:mm') : null),
      hora_fin: citaData.hora_fin || citaData.HoraFin,
      tiempo_total: citaData.tiempo_total || citaData.TiempoTotal || 60,
      motivo: citaData.motivo || citaData.Motivo,
      detalles: citaData.detalles || citaData.Detalles,
      estado_id: citaData.estado_id || citaData.EstadoId,
      servicios_seleccionados: citaData.ServiciosSeleccionados || citaData.servicios_seleccionados
    };
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  fromApi: (apiData) => {
    if (!apiData) return null;
    
    return {
      ...apiData,
      
      // Campos de compatibilidad
      Patente: apiData.patente,
      Fecha: apiData.fecha,
      Hora: apiData.hora,
      HoraFin: apiData.hora_fin,
      TiempoTotal: apiData.tiempo_total || 60,
      Motivo: apiData.motivo,
      Detalles: apiData.detalles,
      Estado: apiData.estado?.nombre || 'Agendada',
      EstadoColor: apiData.estado?.color || '#FFA500',
      ClienteID: apiData.cliente_id,
      ClienteNombre: apiData.cliente ? `${apiData.cliente.nombre} ${apiData.cliente.apellido}`.trim() : '',
      ClienteTelefono: apiData.cliente?.telefono,
      ClienteEmail: apiData.cliente?.email,
      VehiculoInfo: apiData.vehiculo ? `${apiData.vehiculo.marca_local} ${apiData.vehiculo.modelo_local} (${apiData.vehiculo.año})` : '',
      SucursalNombre: apiData.sucursal?.nombre || '',
      ServiciosSeleccionados: apiData.servicios_cita?.map(sc => ({
        id: sc.id,
        ServicioID: sc.servicio?.id,
        ServicioNombre: sc.servicio?.nombre,
        ServicioDescripcion: sc.servicio?.descripcion,
        PrecioEstimado: sc.precio_estimado || 0,
        Observaciones: sc.observaciones,
        CategoriaId: sc.servicio?.categoria_id
      })) || []
    };
  },

  /**
   * Transforma lista de citas
   */
  fromApiList: (apiList) => {
    if (!Array.isArray(apiList)) return [];
    return apiList.map(citaTransforms.fromApi);
  }
};

/**
 * Transformaciones para Vehículos
 */
export const vehiculoTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toApi: (vehiculoData) => {
    return {
      patente: vehiculoData.patente || vehiculoData.Patente,
      marca_local: vehiculoData.marca_local || vehiculoData.Marca,
      modelo_local: vehiculoData.modelo_local || vehiculoData.Modelo,
      año: parseInt(vehiculoData.año || vehiculoData.Año, 10),
      color: vehiculoData.color || vehiculoData.Color,
      vin: vehiculoData.vin || vehiculoData.VIN,
      numero_motor: vehiculoData.numero_motor || vehiculoData.NumeroMotor,
      numero_chasis: vehiculoData.numero_chasis || vehiculoData.NumeroChasis,
      kilometraje: parseInt(vehiculoData.kilometraje || vehiculoData.Kilometraje || 0, 10),
      fecha_compra: vehiculoData.fecha_compra || vehiculoData.FechaCompra,
      precio_compra: vehiculoData.precio_compra || vehiculoData.PrecioCompra,
      propietario_anterior: vehiculoData.propietario_anterior || vehiculoData.PropietarioAnterior,
      observaciones: vehiculoData.observaciones || vehiculoData.Observaciones,
      cliente_id: vehiculoData.cliente_id || vehiculoData.ClienteID || vehiculoData.Cliente?.[0],
      categoria_id: vehiculoData.categoria_id,
      estado_id: vehiculoData.estado_id,
      activo: vehiculoData.activo !== undefined ? vehiculoData.activo : true,
      marca_global_id: vehiculoData.marca_global_id,
      modelo_global_id: vehiculoData.modelo_global_id
    };
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  fromApi: (apiData) => {
    if (!apiData) return null;
    
    return {
      ...apiData,
      
      // ID usando patente para compatibilidad
      id: apiData.patente,
      
      // Campos de compatibilidad
      Patente: apiData.patente,
      Marca: apiData.marca_local,
      Modelo: apiData.modelo_local,
      Año: apiData.año,
      Color: apiData.color,
      VIN: apiData.vin,
      NumeroMotor: apiData.numero_motor,
      NumeroChasis: apiData.numero_chasis,
      Kilometraje: apiData.kilometraje,
      FechaCompra: apiData.fecha_compra,
      PrecioCompra: apiData.precio_compra,
      PropietarioAnterior: apiData.propietario_anterior,
      Observaciones: apiData.observaciones,
      ClienteID: apiData.cliente_id,
      
      // Campos específicos para compatibilidad con filtros y displays
      Cliente: apiData.cliente_id ? [apiData.cliente_id] : [],
      ClienteNombre: apiData.cliente ? `${apiData.cliente.nombre} ${apiData.cliente.apellido}`.trim() : "Desconocido",
      ClienteTelefono: apiData.cliente?.telefono || null,
      
      // Compatibilidad con campos legacy
      MarcaLocal: apiData.marca_local,
      ModeloLocal: apiData.modelo_local,
      CategoriaID: apiData.categoria_id,
      MarcaGlobalID: apiData.marca_global_id,
      ModeloGlobalID: apiData.modelo_global_id
    };
  },

  /**
   * Transforma lista de vehículos
   */
  fromApiList: (apiList) => {
    if (!Array.isArray(apiList)) return [];
    return apiList.map(vehiculoTransforms.fromApi);
  }
};

/**
 * Transformaciones para Mecánicos
 */
export const mecanicoTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toApi: (mecanicoData) => {
    return {
      nombre: mecanicoData.nombre || mecanicoData.Nombre,
      especialidad: mecanicoData.especialidad || mecanicoData.Especialidad,
      telefono: mecanicoData.telefono || mecanicoData.Telefono,
      email: mecanicoData.email || mecanicoData.Email,
      sucursal_id: mecanicoData.sucursal_id || mecanicoData.Sucursal,
      categoria_id: mecanicoData.categoria_id,
      activo: mecanicoData.activo !== undefined ? mecanicoData.activo : (mecanicoData.Activo !== undefined ? mecanicoData.Activo : true)
    };
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  fromApi: (apiData) => {
    if (!apiData) return null;
    
    return {
      ...apiData,
      
      // Campos de compatibilidad
      Nombre: apiData.nombre,
      Especialidad: apiData.especialidad,
      Telefono: apiData.telefono,
      Email: apiData.email,
      Sucursal: apiData.sucursal_id,
      Activo: apiData.activo,
      categoria_id: apiData.categoria_id
    };
  },

  /**
   * Transforma lista de mecánicos
   */
  fromApiList: (apiList) => {
    if (!Array.isArray(apiList)) return [];
    return apiList.map(mecanicoTransforms.fromApi);
  }
};

/**
 * Transformaciones para Patentes y datos de consulta
 */
export const patenteTransforms = {
  /**
   * Transforma datos de cliente desde API de patente al formato del frontend
   */
  clienteFromPatente: (patenteClienteData) => {
    if (!patenteClienteData) return null;
    
    return {
      nombre: patenteClienteData.nombre || '',
      apellido: patenteClienteData.apellido || '',
      nombre_completo: patenteClienteData.nombre_completo || '',
      rut: patenteClienteData.rut || '',
      telefono: patenteClienteData.telefono || '',
      email: patenteClienteData.email || '',
      direccion: patenteClienteData.direccion || '',
      // Campos de compatibilidad
      Nombre: patenteClienteData.nombre || '',
      Apellido: patenteClienteData.apellido || '',
      RUT: patenteClienteData.rut || '',
      Telefono: patenteClienteData.telefono || '',
      Correo: patenteClienteData.email || '',
      Direccion: patenteClienteData.direccion || ''
    };
  },

  /**
   * Transforma datos de vehículo desde API de patente al formato del frontend
   */
  vehiculoFromPatente: (patenteVehiculoData) => {
    if (!patenteVehiculoData) return null;
    
    return {
      patente: patenteVehiculoData.patente || '',
      marca: patenteVehiculoData.marca || '',
      modelo: patenteVehiculoData.modelo || '',
      año: patenteVehiculoData.año || null,
      color: patenteVehiculoData.color || '',
      kilometraje: patenteVehiculoData.kilometraje || '',
      // Usar los IDs mapeados si están disponibles
      marca_local: patenteVehiculoData.marca_local || patenteVehiculoData.marca || '',
      modelo_local: patenteVehiculoData.modelo_local || patenteVehiculoData.modelo || '',
      marca_global_id: patenteVehiculoData.marca_global_id || null,
      modelo_global_id: patenteVehiculoData.modelo_global_id || null,
      // Campos de compatibilidad
      Patente: patenteVehiculoData.patente || '',
      Marca: patenteVehiculoData.marca_local || patenteVehiculoData.marca || '',
      Modelo: patenteVehiculoData.modelo_local || patenteVehiculoData.modelo || '',
      Año: patenteVehiculoData.año || null,
      Color: patenteVehiculoData.color || ''
    };
  },

  /**
   * Función helper para normalizar texto para comparación
   * @param {string} text - Texto a normalizar
   * @returns {string} - Texto normalizado
   */
  normalizeTextForComparison: (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
      .replace(/[^a-z0-9\s]/g, '') // Eliminar caracteres especiales
      .replace(/\s+/g, ' '); // Reemplazar múltiples espacios por uno solo
  },

  /**
   * Función helper para calcular similitud entre dos strings
   * @param {string} str1 - Primer string
   * @param {string} str2 - Segundo string
   * @returns {number} - Porcentaje de similitud (0-1)
   */
  calculateSimilarity: (str1, str2) => {
    const s1 = patenteTransforms.normalizeTextForComparison(str1);
    const s2 = patenteTransforms.normalizeTextForComparison(str2);
    
    if (s1 === s2) return 1; // Coincidencia exacta
    
    // Si uno contiene al otro, alta similitud
    if (s1.includes(s2) || s2.includes(s1)) return 0.8;
    
    // Calcular distancia de Levenshtein simple
    const matrix = [];
    const len1 = s1.length;
    const len2 = s2.length;
    
    for (let i = 0; i <= len2; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len1; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= len2; i++) {
      for (let j = 1; j <= len1; j++) {
        if (s2.charAt(i - 1) === s1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    
    const maxLen = Math.max(len1, len2);
    return maxLen === 0 ? 1 : (maxLen - matrix[len2][len1]) / maxLen;
  },


  /**
   * Función async para mapear marcas y modelos de texto a IDs
   * @param {Object} vehiculoData - Datos del vehículo con marca y modelo como texto
   * @returns {Promise<Object>} - Datos del vehículo con IDs mapeados
   */
  mapearMarcaModeloIds: async (vehiculoData) => {
    if (!vehiculoData) return null;
    
    try {
      const result = { ...vehiculoData };
      
      // Si ya tiene IDs, no mapear de nuevo
      if (result.marca_global_id && result.modelo_global_id) {
        return result;
      }
      
      const marcaTexto = result.marca || result.marca_local;
      
      if (marcaTexto) {
        //console.log(`[PatenteTransforms] Iniciando mapeo para marca: "${marcaTexto}"`);
        
        // Obtener todas las marcas del catálogo de BD
        const marcasResult = await catalogoVehiculosApiService.obtenerMarcas();
        //console.log('[PatenteTransforms] Resultado de obtener marcas:', marcasResult);
        
        // Revisar la estructura de datos del resultado
        //console.log('[PatenteTransforms] Estructura completa de marcasResult:', JSON.stringify(marcasResult, null, 2));
        
        if (marcasResult.success && marcasResult.data) {
          let marcaEncontrada = null;
          let mejorSimilitudMarca = 0;
          
          //console.log(`[PatenteTransforms] Buscando entre ${marcasResult.data.length} marcas disponibles`);
          //console.log('[PatenteTransforms] Primeras 5 marcas:', marcasResult.data.slice(0, 5));
          
          // Buscar marca: primero coincidencia exacta, luego similitud
          for (const marca of marcasResult.data) {
            if (!marca.name) continue;
            
            const similitud = patenteTransforms.calculateSimilarity(marcaTexto, marca.name);
            
            // Coincidencia exacta
            if (similitud === 1) {
              marcaEncontrada = marca;
              break;
            }
            
            // Buscar la mejor similitud (mínimo 70%)
            if (similitud > 0.7 && similitud > mejorSimilitudMarca) {
              mejorSimilitudMarca = similitud;
              marcaEncontrada = marca;
            }
          }
          
          if (marcaEncontrada) {
            result.marca_global_id = marcaEncontrada.id;
            result.marca_local = marcaEncontrada.name;
            
            //console.log(`[PatenteTransforms] Marca mapeada: "${marcaTexto}" → "${marcaEncontrada.name}" (ID: ${marcaEncontrada.id}, similitud: ${mejorSimilitudMarca})`);
            
            // Si hay modelo y se encontró la marca, buscar el modelo
            const modeloTexto = result.modelo || result.modelo_local;
            if (modeloTexto) {
              //console.log(`[PatenteTransforms] Buscando modelo "${modeloTexto}" para marca ${marcaEncontrada.name} (ID: ${marcaEncontrada.id})`);
              const modelosResult = await catalogoVehiculosApiService.obtenerModelos(marcaEncontrada.id);
              
              if (modelosResult.success && modelosResult.data) {
                //console.log(`[PatenteTransforms] Se encontraron ${modelosResult.data.length} modelos para la marca`);
                //console.log(`[PatenteTransforms] Primeros 10 modelos:`, modelosResult.data.slice(0, 10).map(m => ({ id: m.id, name: m.name })));
                
                let modeloEncontrado = null;
                let mejorSimilitudModelo = 0;
                
                // Buscar modelo: primero coincidencia exacta, luego similitud
                for (const modelo of modelosResult.data) {
                  if (!modelo.name) continue;
                  
                  const similitud = patenteTransforms.calculateSimilarity(modeloTexto, modelo.name);
                  //console.log(`[PatenteTransforms] Comparando "${modeloTexto}" con "${modelo.name}" (ID: ${modelo.id}) - similitud: ${similitud}`);
                  
                  // Coincidencia exacta
                  if (similitud === 1) {
                    modeloEncontrado = modelo;
                    //console.log(`[PatenteTransforms] ✅ Coincidencia exacta encontrada!`);
                    break;
                  }
                  
                  // Buscar la mejor similitud (mínimo 60% para modelos, más permisivo)
                  if (similitud > 0.6 && similitud > mejorSimilitudModelo) {
                    mejorSimilitudModelo = similitud;
                    modeloEncontrado = modelo;
                    //console.log(`[PatenteTransforms] 📈 Nueva mejor similitud: ${similitud} para modelo "${modelo.name}"`);
                  }
                }
                
                if (modeloEncontrado) {
                  result.modelo_global_id = modeloEncontrado.id;
                  result.modelo_local = modeloEncontrado.name;
                  
                  //console.log(`[PatenteTransforms] Modelo mapeado: "${modeloTexto}" → "${modeloEncontrado.name}" (ID: ${modeloEncontrado.id}, similitud: ${mejorSimilitudModelo})`);
                } else {
                  //console.log(`[PatenteTransforms] No se encontró modelo similar para: "${modeloTexto}"`);
                  // Mantener el texto original si no se encuentra similar
                  result.modelo_local = modeloTexto;
                }
              }
            }
          } else {
            //console.log(`[PatenteTransforms] No se encontró marca similar para: "${marcaTexto}"`);
            //console.log('[PatenteTransforms] Nombres de marcas disponibles:', marcasResult.data.map(m => m.name).slice(0, 10));
            // Mantener el texto original si no se encuentra similar
            result.marca_local = marcaTexto;
          }
        } else {
          //console.log('[PatenteTransforms] No se pudieron obtener marcas del catálogo');
          //console.log('[PatenteTransforms] marcasResult.success:', marcasResult.success);
          //console.log('[PatenteTransforms] marcasResult.data existe:', !!marcasResult.data);
          //console.log('[PatenteTransforms] marcasResult.disabled:', marcasResult.disabled);
          result.marca_local = marcaTexto;
        }
      }
      
      return result;
      
    } catch (error) {
      console.warn('[PatenteTransforms] Error mapeando IDs de marca/modelo:', error);
      return vehiculoData; // Retornar datos originales si falla el mapeo
    }
  },

  /**
   * Transforma datos de consulta completa de patente
   */
  fromConsultaPatente: (consultaData) => {
    if (!consultaData) return null;
    
    return {
      found: consultaData.found || false,
      success: consultaData.success || false,
      cliente: consultaData.cliente ? patenteTransforms.clienteFromPatente(consultaData.cliente) : null,
      vehiculo: consultaData.vehiculo ? patenteTransforms.vehiculoFromPatente(consultaData.vehiculo) : null,
      source: consultaData.source || 'patentechile.com',
      timestamp: consultaData.timestamp,
      requiresManualInput: consultaData.requiresManualInput || false,
      patente: consultaData.patente || '',
      message: consultaData.message || '',
      error: consultaData.error
    };
  },

  /**
   * Transforma datos de consulta completa de patente con mapeo async de IDs
   * @param {Object} consultaData - Datos de la consulta de patente
   * @returns {Promise<Object>} - Datos transformados con IDs mapeados
   */
  fromConsultaPatenteAsync: async (consultaData) => {
    if (!consultaData) return null;
    
    const baseResult = patenteTransforms.fromConsultaPatente(consultaData);
    
    // Si hay datos de vehículo, mapear los IDs
    if (baseResult.vehiculo) {
      try {
        baseResult.vehiculo = await patenteTransforms.mapearMarcaModeloIds(baseResult.vehiculo);
      } catch (error) {
        console.warn('[PatenteTransforms] Error en mapeo async:', error);
        // Continuar con los datos originales si falla el mapeo
      }
    }
    
    return baseResult;
  },

  /**
   * Transforma datos para crear cliente y vehículo via API
   */
  toCreateClienteVehiculo: (clienteData, vehiculoData) => {
    return {
      cliente: {
        nombre: clienteData.nombre || '',
        apellido: clienteData.apellido || '',
        rut: clienteData.rut || '',
        telefono: clienteData.telefono || '',
        email: clienteData.email || '',
        direccion: clienteData.direccion || ''
      },
      vehiculo: {
        patente: vehiculoData.patente || '',
        marca: vehiculoData.marca || '',
        modelo: vehiculoData.modelo || '',
        año: vehiculoData.año || null,
        color: vehiculoData.color || '',
        kilometraje: vehiculoData.kilometraje || ''
      }
    };
  }
};

/**
 * Transformaciones para Servicios
 */
export const servicioTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toApi: (servicioData) => {
    // Extraer sucursal_id desde diferentes posibles ubicaciones
    let sucursalId = servicioData.sucursal_id || servicioData.Sucursal;
    
    // Si hay DetallesSucursal con SucursalId
    if (servicioData.DetallesSucursal?.SucursalId) {
      sucursalId = servicioData.DetallesSucursal.SucursalId;
    }
    
    return {
      nombre: servicioData.nombre || servicioData.Nombre,
      descripcion: servicioData.descripcion || servicioData.Descripcion,
      precio: servicioData.precio || servicioData.Precio || servicioData.DetallesSucursal?.Precio,
      categoria_id: servicioData.categoria_id || servicioData.CategoriaID,
      tiempo_estimado: servicioData.tiempo_estimado || servicioData.TiempoEstimado,
      garantia_dias: servicioData.garantia_dias || servicioData.GarantiaDias || servicioData.Garantia,
      garantia_kilometros: servicioData.garantia_kilometros || servicioData.GarantiaKilometros || servicioData.GarantiaKms,
      garantia_descripcion: servicioData.garantia_descripcion || servicioData.GarantiaDescripcion || servicioData.NotasEspecificas,
      sucursal_id: sucursalId,
      activo: servicioData.activo !== undefined ? servicioData.activo : true
    };
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  fromApi: (apiData) => {
    if (!apiData) return null;
    
    return {
      ...apiData,
      
      // Campos de compatibilidad
      Nombre: apiData.nombre,
      Descripcion: apiData.descripcion,
      Precio: apiData.precio,
      CategoriaID: apiData.categoria_id,
      TiempoEstimado: apiData.tiempo_estimado,
      Garantia: apiData.garantia_dias,
      GarantiaDias: apiData.garantia_dias,
      GarantiaKilometros: apiData.garantia_kilometros,
      GarantiaDescripcion: apiData.garantia_descripcion,
      Disponible: apiData.activo === true // Campo necesario para filtro frontend
    };
  },

  /**
   * Transforma lista de servicios
   */
  fromApiList: (apiList) => {
    if (!Array.isArray(apiList)) return [];
    return apiList.map(servicioTransforms.fromApi);
  }
};

/**
 * Transformaciones para Sucursales
 */
export const sucursalTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toApi: (sucursalData, empresaId = null) => {
    return {
      nombre: sucursalData.nombre || sucursalData.Nombre,
      direccion: sucursalData.direccion || sucursalData.Direccion,
      telefono: sucursalData.telefono || sucursalData.Telefono,
      ciudad: sucursalData.ciudad || sucursalData.Ciudad,
      horario_atencion: sucursalData.horarios 
        ? (typeof sucursalData.horarios === 'string' ? sucursalData.horarios : JSON.stringify(sucursalData.horarios))
        : sucursalData.horario_atencion || sucursalData.HorarioAtencion,
      horarios: sucursalData.horarios 
        ? (typeof sucursalData.horarios === 'string' ? sucursalData.horarios : JSON.stringify(sucursalData.horarios))
        : null,
      capacidad_clientes: sucursalData.capacidad_clientes || sucursalData.CapacidadClientes || 1,
      empresa_id: empresaId || sucursalData.empresa_id,
      activo: sucursalData.activo !== undefined ? sucursalData.activo : true
    };
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  fromApi: (apiData) => {
    if (!apiData) return null;
    
    return {
      ...apiData,
      
      // Campos de compatibilidad
      Nombre: apiData.nombre,
      Direccion: apiData.direccion,
      Telefono: apiData.telefono,
      Ciudad: apiData.ciudad,
      HorarioAtencion: apiData.horario_atencion,
      horarios: apiData.horario_atencion, // Campo horarios para HorarioSelectorCompact
      CapacidadClientes: apiData.capacidad_clientes || 1
    };
  },

  /**
   * Transforma lista de sucursales
   */
  fromApiList: (apiList) => {
    if (!Array.isArray(apiList)) return [];
    return apiList.map(sucursalTransforms.fromApi);
  }
};


/**
 * Transformaciones para Usuarios
 */
export const usuarioTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toApi: (usuarioData) => {
    return {
      clerk_id: usuarioData.clerk_id,
      email: usuarioData.email || usuarioData.Email,
      nombre: usuarioData.nombre || usuarioData.Nombre,
      apellido: usuarioData.apellido || usuarioData.Apellido,
      telefono: usuarioData.telefono || usuarioData.Telefono,
      tipo_usuario: usuarioData.tipo_usuario || usuarioData.TipoUsuario,
      activo: usuarioData.activo !== undefined ? usuarioData.activo : true
    };
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  fromApi: (apiData) => {
    if (!apiData) return null;
    
    // Log para debugging
    console.log('🔄 [Transform] Datos recibidos de API:', apiData);
    
    const transformed = {
      ...apiData,
      
      // Campos de compatibilidad con valores por defecto
      Email: apiData.email || '',
      Nombre: apiData.nombre || '',
      Apellido: apiData.apellido || '',
      Telefono: apiData.telefono || '',
      TipoUsuario: apiData.tipoUsuario || apiData.tipo_usuario || apiData.TipoUsuario || '',
      
      // Asegurar compatibilidad con campos snake_case y camelCase
      clerk_id: apiData.clerk_id || apiData.clerkId || apiData.ClerkID,
      clerkId: apiData.clerkId || apiData.clerk_id || apiData.ClerkID,
      tipo_usuario: apiData.tipo_usuario || apiData.tipoUsuario || apiData.TipoUsuario,
      
      // ID prioritario para compatibilidad
      id: apiData.id,
      ID: apiData.id,
      
      // Campos adicionales para debugging
      _originalData: apiData
    };
    
    console.log('✅ [Transform] Datos transformados:', transformed);
    return transformed;
  }
};

/**
 * Transformaciones generales
 */
export const generalTransforms = {
  /**
   * Normaliza fechas al formato ISO
   */
  normalizeDate: (date) => {
    if (!date) return null;
    return moment(date).format('YYYY-MM-DD');
  },

  /**
   * Normaliza tiempo al formato HH:mm
   */
  normalizeTime: (time) => {
    if (!time) return null;
    return moment(time, ['HH:mm', 'HH:mm:ss', 'h:mm A']).format('HH:mm');
  },

  /**
   * Normaliza datetime al formato ISO
   */
  normalizeDateTime: (datetime) => {
    if (!datetime) return null;
    return moment(datetime).toISOString();
  },

  /**
   * Limpia string removiendo espacios extra
   */
  cleanString: (str) => {
    if (typeof str !== 'string') return str;
    return str.trim().replace(/\s+/g, ' ');
  },

  /**
   * Convierte a número si es posible
   */
  toNumber: (value) => {
    if (value === null || value === undefined || value === '') return null;
    const num = Number(value);
    return isNaN(num) ? null : num;
  },

  /**
   * Convierte a boolean
   */
  toBoolean: (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' || value === '1';
    }
    return Boolean(value);
  }
};

/**
 * Transformaciones para Pagination
 */
export const paginationTransforms = {
  /**
   * Transforma parámetros de paginación del frontend a la API
   */
  toApi: (paginationData) => {
    return {
      page: paginationData.page || 1,
      limit: paginationData.limit || paginationData.pageSize || 10,
      sort: paginationData.sort,
      order: paginationData.order || 'asc'
    };
  },

  /**
   * Transforma respuesta de paginación de la API al frontend
   */
  fromApi: (apiResponse) => {
    return {
      data: apiResponse.data || [],
      pagination: {
        currentPage: apiResponse.currentPage || 1,
        totalPages: apiResponse.totalPages || 1,
        totalItems: apiResponse.totalItems || 0,
        itemsPerPage: apiResponse.itemsPerPage || 10,
        hasNextPage: apiResponse.hasNextPage || false,
        hasPrevPage: apiResponse.hasPrevPage || false
      }
    };
  }
};

/**
 * Función helper para aplicar múltiples transformaciones
 */
export const applyTransforms = (data, transforms) => {
  if (!data || !transforms) return data;
  
  return transforms.reduce((acc, transform) => {
    if (typeof transform === 'function') {
      return transform(acc);
    }
    return acc;
  }, data);
};

/**
 * Función helper para transformar errores de validación
 */
export const transformValidationErrors = (errors) => {
  if (!errors) return {};
  
  const transformed = {};
  
  if (Array.isArray(errors)) {
    errors.forEach(error => {
      if (error.field) {
        transformed[error.field] = error.message;
      }
    });
  } else if (typeof errors === 'object') {
    Object.keys(errors).forEach(key => {
      transformed[key] = errors[key];
    });
  }
  
  return transformed;
};

/**
 * Transformaciones para Repuestos
 */
export const repuestoTransforms = {
  /**
   * Transforma datos del frontend al formato esperado por la API
   */
  toBackend: (repuestoData) => {
    const result = {};
    
    // Solo incluir campos que tienen valores definidos
    if (repuestoData.nombre !== undefined || repuestoData.Nombre !== undefined) {
      result.nombre = repuestoData.nombre || repuestoData.Nombre;
    }
    
    if (repuestoData.descripcion !== undefined || repuestoData.Descripcion !== undefined) {
      result.descripcion = repuestoData.descripcion || repuestoData.Descripcion;
    }
    
    if (repuestoData.codigo !== undefined || repuestoData.Codigo !== undefined) {
      result.codigo = repuestoData.codigo || repuestoData.Codigo;
    }
    
    if (repuestoData.precio !== undefined || repuestoData.Precio !== undefined || repuestoData.precio_venta !== undefined) {
      result.precio_venta = parseFloat(repuestoData.precio || repuestoData.Precio || repuestoData.precio_venta || 0);
    }
    
    if (repuestoData.precio_compra !== undefined) {
      result.precio_compra = parseFloat(repuestoData.precio_compra || 0);
    }
    
    if (repuestoData.stock !== undefined || repuestoData.Stock !== undefined) {
      result.stock = parseInt(repuestoData.stock || repuestoData.Stock || 0);
    }
    
    if (repuestoData.stock_minimo !== undefined || repuestoData.StockMinimo !== undefined) {
      result.stock_minimo = parseInt(repuestoData.stock_minimo || repuestoData.StockMinimo || 0);
    }
    
    if (repuestoData.categoria_id !== undefined || repuestoData.CategoriaID !== undefined) {
      result.categoria_id = repuestoData.categoria_id || repuestoData.CategoriaID;
    }
    
    if (repuestoData.marca !== undefined || repuestoData.Marca !== undefined) {
      result.marca = repuestoData.marca || repuestoData.Marca || '';
    }
    
    if (repuestoData.ubicacion !== undefined || repuestoData.Ubicacion !== undefined) {
      result.ubicacion = repuestoData.ubicacion || repuestoData.Ubicacion;
    }
    
    if (repuestoData.sucursal_id !== undefined || repuestoData.Sucursal !== undefined) {
      result.sucursal_id = Array.isArray(repuestoData.Sucursal) ? repuestoData.Sucursal[0] : (repuestoData.sucursal_id || repuestoData.Sucursal);
    }
    
    if (repuestoData.activo !== undefined || repuestoData.Disponible !== undefined) {
      result.activo = repuestoData.activo !== undefined ? repuestoData.activo : 
                      (repuestoData.Disponible !== undefined ? repuestoData.Disponible : true);
    }
    
    return result;
  },

  /**
   * Transforma datos de la API al formato esperado por el frontend
   */
  toFrontend: (apiData) => {
    if (!apiData) return null;
    
    return {
      ...apiData,
      
      // Campos de compatibilidad con componentes existentes
      Nombre: apiData.nombre,
      Descripcion: apiData.descripcion,
      Codigo: apiData.codigo,
      Precio: parseFloat(apiData.precio || 0),
      Stock: parseInt(apiData.stock || 0),
      StockMinimo: parseInt(apiData.stock_minimo || 0),
      CategoriaID: apiData.categoria_id,
      Proveedor: apiData.proveedor,
      Ubicacion: apiData.ubicacion,
      Disponible: apiData.activo !== false, // Disponible si activo no es false
      
      // Campos adicionales para compatibilidad
      categoria: apiData.categoria ? {
        id: apiData.categoria.id,
        nombre: apiData.categoria.nombre,
        Nombre: apiData.categoria.nombre
      } : null,
      
      // Información de stock
      StockBajo: apiData.stock <= apiData.stock_minimo,
      PorcentajeStock: apiData.stock_minimo > 0 ? 
        Math.round((apiData.stock / apiData.stock_minimo) * 100) : 100
    };
  },

  /**
   * Transforma lista de repuestos
   */
  fromApiList: (apiList) => {
    if (!Array.isArray(apiList)) return [];
    return apiList.map(repuestoTransforms.toFrontend);
  }
};